// web/src/components/GoLiveChecklist.tsx — "three things from you" (portal spec 14). Phone-first. No time promises.
// Account, plan ($100 a month or $1,000 a year), payments (only when the site takes payments), then preferences and an
// optional photo upload. Each tick reads real data (GET /v1/sites/:id/onboarding). The domain line is shown, not a task:
// KC does the nameservers with the client on a call (D2). No nameserver values here.
import { useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { HomeSite, OnboardingDto, PlanInterval, TickState } from '../lib/types';
import { Icon } from './Icon';
import { toast } from './Toast';
import { PreferencesCard } from './PreferencesCard';

function Row({ state, title, children }: { state: TickState; title: string; children?: ReactNode }) {
  if (state === 'not_needed') return null;
  const done = state === 'done';
  return (
    <div className="rounded-lg border border-hair bg-surface p-4">
      <div className="flex items-center gap-3">
        <span className={`grid size-6 shrink-0 place-items-center rounded-full ${done ? 'bg-run text-runink' : 'border border-hair text-muted'}`}>
          {done ? <Icon name="check" size={14} /> : <span className="size-1.5 rounded-full bg-current" />}
        </span>
        <span className={`flex-1 font-head text-sm ${done ? 'text-muted line-through' : 'text-ink'}`}>{title}</span>
      </div>
      {!done && children && <div className="mt-3 text-sm text-muted sm:pl-9">{children}</div>}
    </div>
  );
}

export function GoLiveChecklist({ site }: { site: HomeSite }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const firstLoad = useRef(true);
  const q = useQuery({
    queryKey: ['onboarding', site.id],
    // The first load bypasses the caches (e.g. right after Stripe Checkout); the 15 s polls read them.
    queryFn: () => { const first = firstLoad.current; firstLoad.current = false; return api.onboarding(site.id, first); },
    refetchInterval: (s) => (s.state.data?.ready ? false : 15_000),
  });
  const o: OnboardingDto | undefined = q.data;
  if (!o) return <div className="h-40 animate-pulse rounded-lg bg-surface" />;

  const startPlan = async (interval: PlanInterval) => {
    setBusy(true);
    try { window.location.href = (await api.checkout(site.id, interval)).url; }
    catch { toast({ kind: 'error', message: 'Couldn’t open checkout. Please try again.' }); setBusy(false); }
  };
  const recheck = async () => {
    setRefreshing(true);
    try { qc.setQueryData(['onboarding', site.id], await api.onboarding(site.id, true)); }
    catch { toast({ kind: 'error', message: 'Couldn’t check right now. Please try again.' }); }
    finally { setRefreshing(false); }
  };

  const heading = o.ready ? 'You’re all set'
    : o.clientDone ? 'Thanks, that’s everything from you'
    : 'We need three things from you';
  const sub = o.ready ? 'We’ll email you when you’re live.'
    : o.clientDone ? 'Next we’ll connect your domain with you on a call.'
    : 'Once we have them, you’re live.';

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div>
        <h2 className="font-head text-lg text-ink">{heading}</h2>
        <p className="mt-1 text-sm text-muted">{sub}</p>
      </div>
      <Row state={o.account} title="Create your account" />
      <Row state={o.plan} title="Choose your plan">
        <p>$100 a month, or $1,000 a year (two months free). Hosting, security, changes when you ask, and a new page every month.</p>
        <p className="mt-2">Your first 30 days are included. We save your card now; your first charge is 30 days after your site goes live.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button disabled={busy} onClick={() => startPlan('month')} className="inline-flex min-h-11 items-center rounded-md bg-run px-4 font-medium text-runink disabled:opacity-50">Monthly · $100</button>
          <button disabled={busy} onClick={() => startPlan('year')} className="inline-flex min-h-11 items-center rounded-md border border-hair px-4 font-medium text-ink disabled:opacity-50">Yearly · $1,000</button>
        </div>
      </Row>
      <Row state={o.stripe} title="Set up payments">
        <p>So your website can take payments. The money goes straight to your account. Your site can go live without it; payments switch on once Stripe is ready.</p>
        <button onClick={() => nav('/stripe')} className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 font-medium text-runink">Set up payments</button>
      </Row>

      <PreferencesCard siteId={site.id} done={o.preferences === 'done'}
        onSaved={() => qc.invalidateQueries({ queryKey: ['onboarding', site.id] })} />

      <div className="rounded-lg border border-hair bg-surface p-4 text-sm text-muted">
        <p className="font-head text-ink">Photos (optional)</p>
        <p className="mt-1">Have photos you’d like on your site? Send them any time.</p>
        <button onClick={() => nav(`/photo?site=${site.id}`)} className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-ink"><Icon name="image" size={16} />Send photos</button>
      </div>

      {o.nameservers !== 'not_needed' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-hair bg-surface p-4 text-sm">
          <span className="text-ink">Your domain: <strong>{o.nameservers === 'done' ? 'Connected' : 'Not yet'}</strong>
            {o.nameservers !== 'done' && <span className="block text-muted">We’ll do this with you on a call.</span>}
          </span>
          {o.nameservers !== 'done' && (
            <button onClick={recheck} disabled={refreshing} className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-ink disabled:opacity-50">
              <Icon name="loader" size={14} />{refreshing ? 'Checking…' : 'Check again'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
